import { Briefcase, Eye, KeyRound, ListCheck, Megaphone, Users } from 'lucide-react';
import React, { useState, useMemo } from 'react';
import { SheetHeader } from 'src/components/ui/sheet';
import { Button } from 'src/components/ui/button';
import { useDispatch } from 'react-redux';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip';
import { useActiveClient } from 'src/redux/active-client/use-active-client';
import BorderedInput from 'src/components/form-new/bordered-input';
import AvatarUpload from 'src/components/form-new/avatar-upload';
import { useGetAccountsQuery, useGetCampaignsQuery } from '../../endpoints/accounts.endpoints';
import { useUpdateClientMutation } from 'src/redux/organization/organization.endpoints';
import notificationService from 'src/services/notification/notification.service';
import { useUploadImageMutation } from 'src/redux/survey/survey.endpoints';

interface ManageClientDrawerHeaderProps {
  activeSelectedAccountIds: string[];
  activeSelectedCampaignIds: string[];
  activeAction: 'permissions' | 'access' | 'users' | 'views';
  actionButtonSlot?: React.ReactNode;
  onActionSelect: (action: 'permissions' | 'access' | 'users' | 'views') => void;
}

const ManageClientDrawerHeader: React.FC<ManageClientDrawerHeaderProps> = ({
  activeSelectedAccountIds,
  activeSelectedCampaignIds,
  activeAction,
  actionButtonSlot = null,
  onActionSelect,
}) => {
  const { activeClient } = useActiveClient();

  const [isLoading, setIsLoading] = useState(false);
  const [clientTitle, setClientTitle] = useState(activeClient?.title ?? '');
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(activeClient?.brand ?? null);

  // const { data: companies } = useGetCompaniesQuery();
  const { data: accountsPage } = useGetAccountsQuery();
  const { data: campaignsPage } = useGetCampaignsQuery();
  const accounts = accountsPage?.items;
  const campaigns = campaignsPage?.items;
  const [updateClient, { isLoading: isLoadingUpdateClient }] = useUpdateClientMutation();
  const [uploadImage, { isLoading: isLoadingUploadImage }] = useUploadImageMutation();

  // Calculate existing accounts and campaigns from activeClient.access
  // Map access.account and access.campaign to ext_account_id and ext_campaign_id
  const existingAccountIds = useMemo(() => {
    if (!activeClient?.access || !accounts) return [];
    // Get unique account IDs from access array
    const accountIds = activeClient.access
      .map(access => {
        // Try to find account by ext_account_id first, then by id
        const account = accounts.find(acc => acc.ext_account_id === access.account || acc.id === access.account);
        return account?.ext_account_id;
      })
      .filter(Boolean) as string[];
    return Array.from(new Set(accountIds));
  }, [activeClient?.access, accounts]);

  const existingCampaignIds = useMemo(() => {
    if (!activeClient?.access || !campaigns) return [];
    // Get unique campaign IDs from access array
    const campaignIds = activeClient.access
      .map(access => {
        // Try to find campaign by ext_campaign_id first, then by id
        const campaign = campaigns.find(camp => camp.ext_campaign_id === access.campaign || camp.id === access.campaign);
        return campaign?.ext_campaign_id;
      })
      .filter(Boolean) as string[];
    return Array.from(new Set(campaignIds));
  }, [activeClient?.access, campaigns]);

  // Check if selections have been initialized
  // Selections are initialized when: we're on access tab AND
  // (selected arrays have items OR existing arrays are empty)
  const isInitialized = useMemo(() => {
    if (activeAction !== 'access') return false;
    // If there are existing items, selections are initialized when selected arrays have items
    // If there are no existing items, we consider it initialized
    if (existingAccountIds.length > 0 || existingCampaignIds.length > 0) {
      // Initialized if selected arrays have been populated (have items)
      return activeSelectedAccountIds.length > 0 || activeSelectedCampaignIds.length > 0;
    }
    return true; // No existing items, so we can show additions
  }, [activeAction, existingAccountIds.length, existingCampaignIds.length, activeSelectedAccountIds.length, activeSelectedCampaignIds.length]);

  // Calculate accounts/campaigns being added (selected but not in existing)
  const addedAccountIds = useMemo(() => {
    // Only calculate when on access tab and initialized
    if (activeAction !== 'access' || !isInitialized) return [];
    return activeSelectedAccountIds.filter(id => !existingAccountIds.includes(id));
  }, [activeSelectedAccountIds, existingAccountIds, activeAction, isInitialized]);

  const addedCampaignIds = useMemo(() => {
    // Only calculate when on access tab and initialized
    if (activeAction !== 'access' || !isInitialized) return [];
    return activeSelectedCampaignIds.filter(id => !existingCampaignIds.includes(id));
  }, [activeSelectedCampaignIds, existingCampaignIds, activeAction, isInitialized]);

  // Calculate accounts/campaigns being removed (in existing but not in selected)
  const removedAccountIds = useMemo(() => {
    // Only calculate when on access tab and initialized
    if (activeAction !== 'access' || !isInitialized) return [];
    return existingAccountIds.filter(id => !activeSelectedAccountIds.includes(id));
  }, [activeSelectedAccountIds, existingAccountIds, activeAction, isInitialized]);

  const removedCampaignIds = useMemo(() => {
    // Only calculate when on access tab and initialized
    if (activeAction !== 'access' || !isInitialized) return [];
    return existingCampaignIds.filter(id => !activeSelectedCampaignIds.includes(id));
  }, [activeSelectedCampaignIds, existingCampaignIds, activeAction, isInitialized]);

  const dispatch = useDispatch();

  const handleClientTitleChange = async(e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setClientTitle(e.target.value);
      if (activeClient?.ext_client_id) {
        await updateClient({
          clientId: activeClient.ext_client_id,
          data: {
            title: e.target.value,
          },
        });
      }
    } catch (error) {
      console.error('Error updating client title:', error);
      notificationService.handleError(error, dispatch, 'Error updating client title');
    }
  };

  const handleUpload = async(file: File) => {
    if (file && activeClient?.ext_client_id) {
      if (file.size > 500 * 1024) { // 500KB in bytes
        notificationService.handleError(null, dispatch, 'File size exceeds 500KB limit');
        setUploadedImage(null);
        return;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'client');
        formData.append('uuid', activeClient.ext_client_id);
        const response = await uploadImage({ formData, uuid: activeClient.ext_client_id }).unwrap();
        setLocalImageUrl(response.item.url);
        await updateClient({
          clientId: activeClient.ext_client_id,
          data: {
            ...activeClient,
            brand: response.item.url,
          },
        });
        setUploadedImage(null);
      } catch (error) {
        console.error('Error uploading image:', error);
        notificationService.handleError(null, dispatch, 'Error uploading image. Please try again.');
      }
    }
  };

  return (
    <SheetHeader className="border-b pb-2">
      {/* top row */}
      <TooltipProvider>
        <div className='flex flex-row items-center relative'>
          {/* BorderedInput on the left */}
          <div className='flex flex-row items-center gap-2'>
            <AvatarUpload
              value={localImageUrl ?? ''}
              onChange={(file: File) => {
                handleUpload(file);
              }}
            />
          </div>
          <div className='text-2xl font-light w-1/3'>
            <BorderedInput
              className='w-full'
              value={clientTitle}
              onChange={(e) => {
                setClientTitle(e.target.value);
              }}
              onBlur={(e) => {
                handleClientTitleChange(e);
              }}
            />
          </div>

          {/* Buttons centered across the entire drawer */}
          <div className='absolute left-1/2 transform -translate-x-1/2'>
            <div className='flex flex-row items-center gap-2 bg-gray-200 p-1 rounded-full'>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size='med'
                    disabled={isLoading}
                    variant='ghost'
                    onClick={() => onActionSelect('permissions')}
                    className={`flex flex-col hover:bg-gray-201 rounded-none px-4 text-sm gap-1 border-gray-201 rounded-full ${activeAction === 'permissions' ? 'bg-blue-500 text-white hover:bg-blue-500/80' : ''}`}
                  >
                    <KeyRound className='h-4 w-4' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Manage Client Permissions
                  </p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size='med'
                    disabled={isLoading }
                    variant='ghost'
                    onClick={() => onActionSelect('access')}
                    className={`flex flex-col hover:bg-gray-201 rounded-none px-4 text-sm gap-1 border-gray-201 rounded-full ${activeAction === 'access' ? 'bg-blue-500 text-white hover:bg-blue-500/80' : ''}`}
                  >
                    <ListCheck className='h-4 w-4' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Mange Client Access
                  </p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size='med'
                    variant='ghost'
                    onClick={(() => onActionSelect('views'))}
                    disabled={isLoading}
                    title='Add Line Item to Invoice'
                    className={`flex flex-col hover:bg-gray-201 rounded-none px-4 text-sm gap-1 border-gray-201 rounded-full ${activeAction === 'views' ? 'bg-blue-500 text-white hover:bg-blue-500/80' : ''}`}
                  >
                    <Eye className='h-4 w-4' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Manage Client Views
                  </p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size='med'
                    disabled={isLoading}
                    variant='ghost'
                    onClick={() => onActionSelect('users')}
                    className={`flex flex-col hover:bg-gray-201 rounded-none px-4 text-sm gap-1 border-gray-201 rounded-full ${activeAction === 'users' ? 'bg-blue-500 text-white hover:bg-blue-500/80' : ''}`}
                  >
                    <Users className='h-4 w-4' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Mange Client Users
                  </p>
                </TooltipContent>
              </Tooltip>

            </div>
          </div>
        </div>

        {/* bottom row */}
        <div className='flex flex-row items-center justify-between relative'>
          <div className='flex flex-row items-center gap-2 border-gray-201'>
            <div className='mr-6'>
              <span className='text-gray-800 text-sm'>
                Users
              </span>
              <div className='flex flex-row items-center gap-2 text-2xl font-bold'>
                <Users className='h-5 w-5' />
                {activeClient?.users?.length ?? 0}
              </div>
            </div>
            <div className='mr-6'>
              <span className='text-gray-800 text-sm'>
                Accounts
              </span>
              <div className='flex flex-row items-center text-2xl font-bold relative'>
                <Briefcase className='h-5 w-5 mr-2' />
                {existingAccountIds.length}
                {addedAccountIds.length > 0 && (
                  <span className='text-green-501 text-lg'>
                    {' '}
                    +
                    {addedAccountIds.length}
                  </span>
                )}
                {removedAccountIds.length > 0 && (
                  <span className='text-red-500 text-lg'>
                    {' '}
                    -
                    {removedAccountIds.length}
                  </span>
                )}
              </div>
            </div>
            <div className=''>
              <span className='text-gray-800 text-sm'>
                Campaigns
              </span>
              <div className='flex flex-row items-center text-2xl font-bold relative'>
                <Megaphone className='h-5 w-5 mr-2' />
                {existingCampaignIds.length}
                {addedCampaignIds.length > 0 && (
                  <span className='text-green-501 text-lg'>
                    {' '}
                    +
                    {addedCampaignIds.length}
                  </span>
                )}
                {removedCampaignIds.length > 0 && (
                  <span className='text-red-500 text-lg'>
                    {' '}
                    -
                    {removedCampaignIds.length}
                  </span>
                )}
              </div>
            </div>
          </div>
          {activeAction && (
            <div className='absolute left-1/2 transform -translate-x-1/2'>
              <div className='text-lg text-gray-500 font-light'>
                {activeAction.toUpperCase()}
              </div>
            </div>
          )}
          <div className='flex flex-row items-center gap-2'>
            {actionButtonSlot}
          </div>

        </div>

      </TooltipProvider>

    </SheetHeader>
  );
};

export default ManageClientDrawerHeader;
